import './articlegrid.less'

import {Fragment, FunctionComponent} from 'react'
import {CardData, TagListItem} from 'types'
import {Mod, style} from 'util/style'
import {
  Card,
  CardCategory,
  CardContainer,
  CardDate,
  CardDownloadIcon,
  CardFooter,
  CardHeader,
  CardHeaderCol,
  CardHeaderRow,
  CardImage,
  CardSeparator,
  CardShareIcon,
  CardTag,
  CardTitle
} from 'views/layout/card'

import {m} from '@codeurs/front'
import downloadicon from 'assets/icons/download.png'
import format from 'date-fns/format'
import {SizeMe} from 'react-sizeme'
import {formatDate} from 'util/formatDate'
import {useLanguage} from 'util/i18n'
import {PageLink} from 'util/pagelink'
import {getLocale} from 'util/params'
import {Theme} from 'views/theme'

export type ArticleGridType = {}

const styles = style('articlegrid', ['card'])

export const ArticleGrid: FunctionComponent<
  ArticleGridType & {
    hasMore: boolean
    blue?: boolean
    mod?: Mod<'homecbam'>
  }
> = ({hasMore, children, blue, mod}) => {
  return (
    <div class={styles('container')}>
      <Theme.Container>
        <div class={styles.mod({hasmore: hasMore, blue}).mod(mod)}>
          {children}
        </div>
      </Theme.Container>
    </div>
  )
}

export type ArticleCardType = {lang: string}

const ViewCardTags: FunctionComponent<{
  itemTags: Array<string>
  tagspath: string
  tagslist: Array<TagListItem>
}> = ({itemTags, tagspath, tagslist}) => {
  return (
    <Fragment>
      {itemTags.map((tag, i) => {
        const tagItem = tagslist.find(item => item.tag === tag)
        if (!tagItem) return null
        return (
          <Fragment>
            {i > 0 && <CardSeparator />}
            <PageLink to={tagspath + '?tag=' + tagItem.slug}>
              <CardTag>{tag}</CardTag>
            </PageLink>
          </Fragment>
        )
      })}
    </Fragment>
  )
}

export const ArticleCard: FunctionComponent<{
  item: CardData & {key?: number}
  tagspath: string
  tagslist: Array<TagListItem>
  half?: boolean
}> = ({item, tagspath, tagslist, half}) => {
  return (
    <div {...styles.card('gaps').mod({half})}>
      <Card {...styles.card.mod({noimage: !item.image})}>
        {item.image && (
          <PageLink
            to={item.url}
            aria-label={item.homepage_title || item.title}
          >
            <CardImage {...item.image} />
          </PageLink>
        )}
        <CardContainer>
          <CardHeader>
            <CardHeaderCol>
              {item.category &&
                (item.category.link ? (
                  <div className={styles.card('gaps-category')}>
                    <PageLink to={item.category.link}>
                      <CardCategory color={item.category.color}>
                        {item.category.title}
                      </CardCategory>
                    </PageLink>
                  </div>
                ) : (
                  <CardCategory color={item.category.color}>
                    {item.category.title}
                  </CardCategory>
                ))}
              {item.date && <CardDate>{formatDate(item.date)}</CardDate>}
            </CardHeaderCol>
            <CardHeaderRow>
              {!!(item.media && item.media.file) && (
                <CardDownloadIcon
                  image={{...downloadicon, alt: 'Download icon'}}
                  url={item.media.file.url}
                  ariaLabel={`Download ${item.homepage_title || item.title}`}
                />
              )}
              {item.share && (
                <CardShareIcon copy={item.title} url={item.share} />
              )}
            </CardHeaderRow>
          </CardHeader>
          {(item.homepage_title || item.title) && (
            <PageLink to={item.url}>
              <SizeMe monitorHeight>
                {({size}) => (
                  <CardTitle
                    as="span"
                    class={styles.card('title').mod({
                      fade:
                        (size.height >= 68 && size.height <= 72) ||
                        size.height > 78
                    })}
                  >
                    {item.homepage_title ? item.homepage_title : item.title}
                  </CardTitle>
                )}
              </SizeMe>
            </PageLink>
          )}
          {item.tags_to_show && item.tags.length > 0 && (
            <CardFooter>
              <ViewCardTags
                itemTags={item.tags_to_show}
                tagspath={tagspath}
                tagslist={tagslist}
              />
            </CardFooter>
          )}
        </CardContainer>
      </Card>
    </div>
  )
}

export const LibraryCard: FunctionComponent<{
  item: CardData & {key: number}
  tagspath: string
  tagslist: Array<TagListItem>
  half?: boolean
}> = ({item, tagspath, tagslist, half}) => {
  const getPublishedDate = () => {
    switch (item.channel) {
      case 'link_item':
        return null
      case 'presentation':
        if (!item.date) return null
        return (
          <CardDate>
            {format(new Date(item.date.replace(/-/g, '/')), 'MMMM Y', {
              locale: getLocale(useLanguage())
            })}
          </CardDate>
        )
      default:
        if (!item.date) return null
        return <CardDate>{formatDate(item.date)}</CardDate>
    }
  }

  const hasDownload = !!(item.media && item.media.file)

  return (
    <div {...styles.card('gaps').mod({half})}>
      <Card {...styles.card}>
        {item.image && (
          <PageLink to={item.url} aria-label={item.title}>
            <CardImage {...item.image} />
          </PageLink>
        )}
        <CardContainer>
          <CardHeader>
            <CardHeaderCol>{getPublishedDate()}</CardHeaderCol>
            <CardHeaderRow>
              {!!(item.media && item.media.file) && (
                <CardDownloadIcon
                  image={{...downloadicon, alt: 'Download icon'}}
                  url={item.media.file.url}
                  ariaLabel={`Download ${item.title}`}
                />
              )}
              {item.share && (
                <CardShareIcon copy={item.title} url={item.share} />
              )}
            </CardHeaderRow>
          </CardHeader>
          {item.title && (
            <PageLink to={item.url}>
              <CardTitle>{item.title}</CardTitle>
            </PageLink>
          )}
          {!item.image && item.text && (
            <p {...styles.card('text')}>{item.text}</p>
          )}
          {item.tags_to_show && item.tags.length !== 0 && (
            <CardFooter>
              <ViewCardTags
                itemTags={item.tags_to_show}
                tagspath={tagspath}
                tagslist={tagslist}
              />
            </CardFooter>
          )}
        </CardContainer>
      </Card>
    </div>
  )
}
